import React, { useEffect, useState } from "react";
import { Dialog } from "../Dialog";
import MetadataEditor from "../Metadata/MetadataEditor";
import { KNOWN_DATA_TYPES, appDataRowEndpoint } from "../../utils/appData";
import RowEndpoint from "./RowEndpoint";
import { ErrorBanner, Field, Hint, Input, Label, Select } from "./AppDataStyles";

// Create/edit one app data row. Fields mirror the backend row: name, key, data_type, data_source,
// data and locked. Editing a locked row requires an explicit force.
//
// The data editor depends on the row: JSON values get the structured editor (with a JSON-text
// toggle), anything else is a plain text field.

const empty = {
  name: "",
  key: "",
  data_type: "json",
  data_source: "admin",
  locked: false,
};

const isJsonValue = (value) => value !== null && typeof value === "object";

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {"create"|"edit"} props.mode
 * @param {object|null} [props.row] The row being edited.
 * @param {"global"|"user"} props.scope
 * @param {string} [props.defaultName]
 * @param {string} [props.userLabel]
 * @param {string|null} [props.clientId] For the endpoint hint.
 * @param {string} [props.baseUrl] The API base, for the endpoint hint.
 * @param {boolean} [props.saving]
 * @param {string|null} [props.error]
 * @param {Function} props.onClose
 * @param {(payload: object) => void} props.onSave
 */
export const AppDataRowDialog = ({
  show,
  mode = "create",
  row = null,
  scope = "global",
  defaultName = "",
  userLabel = null,
  clientId = null,
  baseUrl = "",
  saving = false,
  error = null,
  onClose = () => {},
  onSave = () => {},
}) => {
  const [form, setForm] = useState(empty);
  const [dataText, setDataText] = useState("");
  const [jsonRow, setJsonRow] = useState(true);
  const [createMode, setCreateMode] = useState("upsert");
  const [force, setForce] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!show) return;

    const source = mode === "edit" && row ? row : null;
    const value = source?.data ?? null;
    const json = source ? source.data_type === "json" || isJsonValue(value) : true;

    setForm(
      source
        ? {
            name: source.name ?? "",
            key: source.key ?? "",
            data_type: source.data_type ?? "json",
            data_source: source.data_source ?? "admin",
            locked: Boolean(source.locked),
          }
        : { ...empty, name: defaultName }
    );

    setJsonRow(json);
    setDataText(
      json
        ? JSON.stringify(value ?? {}, null, 2)
        : value === null || value === undefined
          ? ""
          : String(value)
    );
    setCreateMode("upsert");
    setForce(false);
    setLocalError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, mode, row]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = () => {
    if (saving) return;

    if (!String(form.name).trim()) {
      setLocalError("A name is required.");
      return;
    }

    if (!String(form.key).trim()) {
      setLocalError("A key is required.");
      return;
    }

    if (mode === "edit" && row?.locked && !force) {
      setLocalError("This row is locked — enable force to update it.");
      return;
    }

    let data;

    if (jsonRow) {
      const text = dataText.trim();

      if (text === "") {
        data = null;
      } else {
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          setLocalError(`Data is not valid JSON: ${parseError.message}`);
          return;
        }
      }
    } else {
      data = dataText;
    }

    setLocalError(null);

    onSave({
      name: String(form.name).trim(),
      key: String(form.key).trim(),
      data_type: form.data_type,
      data_source: form.data_source,
      data,
      locked: Boolean(form.locked),
      ...(mode === "create" ? { mode: createMode } : {}),
      ...(force ? { force: true } : {}),
    });
  };

  const endpoint = appDataRowEndpoint({
    baseUrl,
    scope,
    name: form.name,
    key: form.key,
    publicId: row?.public_id ?? null,
    clientId,
  });

  return (
    <Dialog
      title={mode === "edit" ? "Edit app data row" : "New app data row"}
      subtitle={
        scope === "user"
          ? `User scope${userLabel ? ` · ${userLabel}` : ""}`
          : "Global scope (shared by every user in this client)"
      }
      show={show}
      onClose={onClose}
      onOK={submit}
      okText={saving ? "Saving…" : "Save"}
      closeText="Cancel"
      style={{ width: "90%", maxWidth: "880px", height: "88vh" }}
    >
      <Field>
        <Label>Name</Label>
        <Input
          value={form.name}
          disabled={mode === "edit"}
          placeholder="onboarding_questions"
          onChange={(event) => update("name", event.target.value)}
        />
        <Hint>Rows sharing a name form a group.</Hint>
      </Field>

      <Field>
        <Label>Key</Label>
        <Input
          value={form.key}
          disabled={mode === "edit"}
          placeholder="v1"
          onChange={(event) => update("key", event.target.value)}
        />
      </Field>

      <Field>
        <Label>Data type</Label>
        <Input
          list="app-data-types"
          value={form.data_type}
          onChange={(event) => {
            update("data_type", event.target.value);
            // "json" gets the structured editor; other types are plain text.
            setJsonRow(event.target.value === "json" || jsonRow);
          }}
        />
        <datalist id="app-data-types">
          {KNOWN_DATA_TYPES.map((type) => (
            <option key={type} value={type} />
          ))}
        </datalist>
        <Hint>Free text; these are the values currently in use.</Hint>
      </Field>

      <Field>
        <Label>Data source</Label>
        <Input
          value={form.data_source}
          placeholder="admin"
          onChange={(event) => update("data_source", event.target.value)}
        />
      </Field>

      {jsonRow ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Label>Data</Label>
          <MetadataEditor value={dataText} onChange={setDataText} />
        </div>
      ) : (
        <Field>
          <Label>Data</Label>
          <Input
            value={dataText}
            placeholder="plain text value"
            onChange={(event) => setDataText(event.target.value)}
          />
          <Hint>This row's data is not JSON, so it is edited as text.</Hint>
        </Field>
      )}

      <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12.5px" }}>
        <input type="checkbox" checked={form.locked} onChange={(event) => update("locked", event.target.checked)} />
        Locked
      </label>

      {mode === "create" ? (
        <Field>
          <Label>If the row already exists</Label>
          <Select value={createMode} onChange={(event) => setCreateMode(event.target.value)}>
            <option value="upsert">Upsert — create or update</option>
            <option value="create">Create only — fail if it exists</option>
          </Select>
        </Field>
      ) : null}

      {mode === "edit" && row?.locked ? (
        <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12.5px", color: "#f39c12" }}>
          <input type="checkbox" checked={force} onChange={(event) => setForce(event.target.checked)} />
          This row is locked — force the update
        </label>
      ) : null}

      {endpoint ? (
        <Field>
          <Label>Retrieve this row</Label>
          <RowEndpoint url={endpoint} />
        </Field>
      ) : null}

      {localError ? <ErrorBanner>{localError}</ErrorBanner> : null}
      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
    </Dialog>
  );
};

export default AppDataRowDialog;
